import { CommonModule } from "@angular/common";
import { NgModule } from "@angular/core";
import { ReactiveFormsModule } from "@angular/forms";
import { NgbDatepickerModule } from "@ng-bootstrap/ng-bootstrap";
import { NgSelectModule } from "@ng-select/ng-select";
import { TranslateModule } from "@ngx-translate/core";
import { GeneralFacility } from "components/facilities/general-facility/general-facility";
import { RatesFacilityCareLevel } from "components/facilities/rates-facility-care-level/rates-facility-care-level";
import { RatesFacility } from "components/facilities/rates-facility/rates-facility";
import { IonicPageModule } from "ionic-angular";
import { IonicModule } from "ionic-angular/module";
import { TruncateModule } from "ng2-truncate";
import { PatientHistoryGridComponent } from "../pages/patient-chart/patient-history-grid/patient-history-grid";
import { ActivityLogGridComponent } from "./activity-log-grid/activity-log-grid";
import { CardSectionHeaderComponent } from "./card-section-header/card-section-header.component";
import { CheckboxGroupWithInputComponent } from "./checkbox-group-with-input/checkbox-group-with-input.component";
import { CollapsibleCardComponent } from "./collapsible-card/collapsible-card.component";
import { CommonComponentsModule } from "./common/common-components.module";
import { CompanyResourcesDetailComponent } from "./company-resources-detail/company-resources-detail";
import { VolunteerDetailsComponent } from "./company-resources-detail/volunteer-details/volunteer-details";
import { CompanyResourcesGridComponent } from "./company-resources-grid/company-resources-grid";
import { ConfigurableInputsModule } from "./configurable/inputs/configurable-inputs.module";
import { SelectFormInputComponent } from "./configurable/inputs/select-form-input/select-form-input";
import { DataDisplayWithTooltipComponent } from "./data-display-with-tooltip/data-display-with-tooltip.component";
import { DataTableComponent } from "./data-table/data-table.component";
import { DateTimeFieldComponent } from "./date-time-field/date-time-field.component";
import { DownloadButtonComponent } from "./download-button/download-button.component";
import { FaxModalComponent } from "./fax/fax-modal/fax-modal";
import { FormActionButtonsComponent } from "./form-action-buttons/form-action-buttons.component";
import { ChangeHistoryPanelComponent } from "./change-history-panel/change-history-panel";
import { GridPagerComponent } from "./grid-pager/grid-pager";
import { GenerateAISummary } from "./intake-queue/generate-ai-summary-modal/generate-ai-summary-modal";
import { PatientCareDirectivesPage } from "./intake-queue/patient-care-directives/patient-care-directives";
import { PatientDetailsGridComponent } from "./intake-queue/patient-details-grid/patient-details-grid";
import { PatientDiagnosisGridComponent } from "./intake-queue/patient-diagnosis-grid/patient-diagnosis-grid";
import { PatientDocumentsComponent } from "./intake-queue/patient-documents/patient-documents";
import { MarTileComponent } from "./medication-administration-records/mar-tile/mar-tile";
import { MedicationAdministrationRecordComponent } from "./medication-administration-records/medication-administration-record/medication-administration-record";
import { MedicationHistoryComponent } from "./medication-administration-records/medication-history/medication-history";
import { MedicationListComponent } from "./medication-administration-records/medication-list/medication-list";
import { MedicationSummaryComponent } from "./medication-administration-records/medication-summary/medication-summary";
import { MedicationComponent } from "./medication-administration-records/medication/medication";
import { MultiSelectDrugComponent } from "./multi-select-drug/multi-select-drug";
import { OMDetailsComponent } from "./order-management/om-details/om-details";
import { WrappedReasonsForGIPComponent } from "./order-management/om-details/reasons-for-gip/reasons-for-gip";
import { WrappedTextComponent } from "./order-management/om-details/wrapped-text/wrapped-text";
import { OrderManagementComponentsModule } from "./order-management/order-management.module";
import { DiagnosisOrderFormComponent } from "./orders/forms/diagnosis-order-form/diagnosis-order-form";
import { DmeOrderFormComponent } from "./orders/forms/dme-order-form/dme-order-form";
import { LevelOfCareOrderFormComponent } from "./orders/forms/level-of-care-order-form/level-of-care-order-form";
import { MedicationOrderFormComponent } from "./orders/forms/medication-order-form/medication-order-form";
import { OtherOrderFormComponent } from "./orders/forms/other-order-form/other-order-form";
import { RecertificationOrderFormComponent } from "./orders/forms/recertification-order-form/recertification-order-form";
import { StandingOrderFormComponent } from "./orders/forms/standing-order-form/standing-order-form";
import { SupplyOrderFormComponent } from "./orders/forms/supply-order-form/supply-order-form";
import { TreatmentOrderFormComponent } from "./orders/forms/treatment-order-form/treatment-order-form";
import { VisitFrequencyOrderFormComponent } from "./orders/forms/visit-frequency-order-form/visit-frequency-order-form";
import { OrderContainerComponent } from "./orders/order-container/order-container";
import { OrderItemComponent } from "./orders/order-item/order-item";
import { OrderPopupComponent } from "./orders/order-popup/order-popup";
import { BillingCodesRnb } from "./payers/billing-codes/billing-codes-rnb/billing-codes-rnb";
import { BillingCodesTable } from "./payers/billing-codes/billing-codes-table/billing-codes-table";
import { BillingCodesHospiceLOC } from "./payers/billing-codes/hospice-loc/hospice-loc";
import { BillingCodesHospiceVisits } from "./payers/billing-codes/hospice-visits/hospice-visits";
import { PalliativeCodesVisitsTable } from "./payers/billing-codes/palliative-visits/palliative-codes-visits-table/palliative-codes-visits-table";
import { BillingCodesPalliativeVisits } from "./payers/billing-codes/palliative-visits/palliative-visits";
import { BillingOptions } from "./payers/billing-options/billing-options";
import { GeneralPayer } from "./payers/general-payer/general-payer";
import { PeriodOptions } from "./payers/period-options/period-options";
import { RatesHospiceLOC } from "./payers/rates-payer/hospice-loc/hospice-loc";
import { RatesHospiceVisits } from "./payers/rates-payer/hospice-visits/hospice-visits";
import { HospiceVisitsTable } from "./payers/rates-payer/hospice-visits/hospice-visits-table/hospice-visits-table";
import { RatesPalliativeVisits } from "./payers/rates-payer/palliative-visits/palliative-visits";
import { PalliativeVisitsTable } from "./payers/rates-payer/palliative-visits/palliative-visits-table/palliative-visits-table";
import { RatesPayer } from "./payers/rates-payer/rates-payer";
import { RatesRnb } from "./payers/rates-payer/rates-rnb/rates-rnb";
import { PhysicianSearchComponent } from "./physician-search/physician-search";
import { UsersGridComponent } from "./qa-control/users-grid/users-grid";
import { VisitTypesGridComponent } from "./qa-control/visit-types-grid/visit-types-grid";
import { RadioGroupWithInputComponent } from "./radio-group-with-input/radio-group-with-input.component";
import { StaticCardComponent } from "./static-card/static-card.component";
import { ActivityLogAddComponent } from "./system-admin/activity-log-add/activity-log-add";
import { ActivityLogDetailsComponent } from "./system-admin/activity-log-details/activity-log-details";
import { ActivityLogComponent } from "./system-admin/activity-log/activity-log";
import { AddUserToRole } from "./system-admin/add-user-to-role/add-user-to-role";
import { VerticalTabs } from "./system-admin/vertical-tabs/vertical-tabs";
import { ViewRoleDetails } from "./system-admin/view-role-details/view-role-details";
import { MultiSiteTeamComponent } from "./system-admin/workforce/multi-site-team/multi-site-team";
import { WorkforceAccountContactDetails } from "./system-admin/workforce/workforce-account-contact/workforce-account-contact-details";
import { WorkforceContactDetails } from "./system-admin/workforce/workforce-contact-details/workforce-contact-details";
import { WorkforceGeneralContact } from "./system-admin/workforce/workforce-general-contact/workforce-general-contact";
import { WorkforceJobRoleDetails } from "./system-admin/workforce/workforce-job-role-details/workforce-job-role-details";
import { WorkforcePhoneInfoComponent } from "./system-admin/workforce/workforce-phone-info/workforce-phone-info";
import { WorkforceResourcesDetailComponent } from "./system-admin/workforce/workforce-resources-detail/workforce-resources-detail";
import { WorkforceResourcesGridComponent } from "./system-admin/workforce/workforce-resources-grid/workforce-resources-grid";
import { WorkforceScheduleContactDetails } from "./system-admin/workforce/workforce-schedule/workforce-schedule-contact-details";
import { WorkforceSkillsAndInterests } from "./system-admin/workforce/workforce-skills-and-interests/workforce-skills-and-interests.component";
import { TabsComponent } from "./tabs/tabs.component";
import { TwoColumnInputComponent } from "./two-column-input/two-column-input.component";

@NgModule({
  declarations: [
    PatientCareDirectivesPage,
    OrderContainerComponent,
    OrderItemComponent,
    LevelOfCareOrderFormComponent,
    DiagnosisOrderFormComponent,
    RecertificationOrderFormComponent,
    OtherOrderFormComponent,
    VisitFrequencyOrderFormComponent,
    TreatmentOrderFormComponent,
    SelectFormInputComponent,
    SupplyOrderFormComponent,
    DmeOrderFormComponent,
    MedicationOrderFormComponent,
    OrderPopupComponent,
    FaxModalComponent,
    MedicationComponent,
    MedicationAdministrationRecordComponent,
    MedicationSummaryComponent,
    MedicationHistoryComponent,
    MarTileComponent,
    MedicationListComponent,
    MultiSelectDrugComponent,
    StandingOrderFormComponent,
    ChangeHistoryPanelComponent,
    GridPagerComponent,
    PhysicianSearchComponent,
    ActivityLogGridComponent,
    CompanyResourcesGridComponent,
    CompanyResourcesDetailComponent,
    VolunteerDetailsComponent,
    PatientHistoryGridComponent,
    OMDetailsComponent,
    WrappedTextComponent,
    WrappedReasonsForGIPComponent,
    MultiSiteTeamComponent,
    WorkforceResourcesGridComponent,
    WorkforceResourcesDetailComponent,
    ActivityLogComponent,
    ActivityLogDetailsComponent,
    VerticalTabs,
    ActivityLogAddComponent,
    WorkforceGeneralContact,
    WorkforceJobRoleDetails,
    AddUserToRole,
    ViewRoleDetails,
    WorkforceAccountContactDetails,
    WorkforceContactDetails,
    WorkforceSkillsAndInterests,
    WorkforcePhoneInfoComponent,
    WorkforceScheduleContactDetails,
    VisitTypesGridComponent,
    UsersGridComponent,
    VisitTypesGridComponent,
    PatientDetailsGridComponent,
    PatientDiagnosisGridComponent,
    PatientDocumentsComponent,
    GenerateAISummary,
    UsersGridComponent,
    TabsComponent,
    GeneralFacility,
    GeneralPayer,
    RatesFacility,
    RatesFacilityCareLevel,
    BillingCodesTable,
    RatesFacilityCareLevel,
    RatesPayer,
    RatesHospiceLOC,
    RatesHospiceVisits,
    RatesRnb,
    HospiceVisitsTable,
    RatesPalliativeVisits,
    PalliativeVisitsTable,
    DataDisplayWithTooltipComponent,
    BillingCodesHospiceLOC,
    BillingCodesHospiceVisits,
    BillingCodesRnb,
    BillingOptions,
    BillingCodesPalliativeVisits,
    PalliativeCodesVisitsTable,
    CollapsibleCardComponent,
    DataTableComponent,
    StaticCardComponent,
    DownloadButtonComponent,
    CardSectionHeaderComponent,
    RadioGroupWithInputComponent,
    DateTimeFieldComponent,
    CheckboxGroupWithInputComponent,
    TwoColumnInputComponent,
    FormActionButtonsComponent,
    PeriodOptions,
  ],
  imports: [
    CommonModule,
    ConfigurableInputsModule,
    NgSelectModule,
    ReactiveFormsModule,
    TranslateModule.forChild(),
    IonicPageModule.forChild(OrderItemComponent),
    CommonComponentsModule,
    TruncateModule,
    NgbDatepickerModule,
    OrderManagementComponentsModule,
    IonicModule,
  ],
  exports: [
    PatientCareDirectivesPage,
    OrderContainerComponent,
    OrderItemComponent,
    LevelOfCareOrderFormComponent,
    DiagnosisOrderFormComponent,
    RecertificationOrderFormComponent,
    OtherOrderFormComponent,
    VisitFrequencyOrderFormComponent,
    TreatmentOrderFormComponent,
    SelectFormInputComponent,
    SupplyOrderFormComponent,
    DmeOrderFormComponent,
    MedicationOrderFormComponent,
    OrderPopupComponent,
    FaxModalComponent,
    MedicationComponent,
    MedicationAdministrationRecordComponent,
    MedicationSummaryComponent,
    MedicationHistoryComponent,
    MarTileComponent,
    MedicationListComponent,
    MultiSelectDrugComponent,
    StandingOrderFormComponent,
    ChangeHistoryPanelComponent,
    GridPagerComponent,
    PhysicianSearchComponent,
    ActivityLogGridComponent,
    CompanyResourcesGridComponent,
    CompanyResourcesDetailComponent,
    VolunteerDetailsComponent,
    PatientHistoryGridComponent,
    OMDetailsComponent,
    WrappedTextComponent,
    WrappedReasonsForGIPComponent,
    MultiSiteTeamComponent,
    WorkforceResourcesGridComponent,
    VisitTypesGridComponent,
    PatientDetailsGridComponent,
    PatientDiagnosisGridComponent,
    PatientDocumentsComponent,
    GenerateAISummary,
    UsersGridComponent,
    WorkforceResourcesDetailComponent,
    ActivityLogComponent,
    ActivityLogDetailsComponent,
    VerticalTabs,
    ActivityLogAddComponent,
    WorkforceGeneralContact,
    WorkforceJobRoleDetails,
    AddUserToRole,
    ViewRoleDetails,
    WorkforceAccountContactDetails,
    WorkforceContactDetails,
    WorkforceSkillsAndInterests,
    WorkforcePhoneInfoComponent,
    WorkforceScheduleContactDetails,
    WorkforceContactDetails,
    TabsComponent,
    GeneralFacility,
    GeneralPayer,
    RatesFacility,
    RatesFacilityCareLevel,
    BillingCodesTable,
    RatesFacilityCareLevel,
    RatesPayer,
    RatesHospiceLOC,
    RatesHospiceVisits,
    RatesRnb,
    HospiceVisitsTable,
    RatesPalliativeVisits,
    PalliativeVisitsTable,
    DataDisplayWithTooltipComponent,
    BillingCodesHospiceLOC,
    BillingCodesHospiceVisits,
    BillingCodesRnb,
    BillingOptions,
    BillingCodesPalliativeVisits,
    PalliativeCodesVisitsTable,
    CollapsibleCardComponent,
    DataTableComponent,
    StaticCardComponent,
    DownloadButtonComponent,
    CardSectionHeaderComponent,
    RadioGroupWithInputComponent,
    DateTimeFieldComponent,
    CheckboxGroupWithInputComponent,
    TwoColumnInputComponent,
    FormActionButtonsComponent,
    PeriodOptions,
  ],
  entryComponents: [
    ActivityLogComponent,
    ActivityLogAddComponent,
    GenerateAISummary,
    FaxModalComponent,
  ],
})
export class ComponentsModule {
  static getActivityLogComponent() {
    return ActivityLogComponent;
  }
}



// WEBPACK FOOTER //
// ./src/components/components.module.ts